import { Plus, Trash2, ChevronRight, ChevronDown, GitFork } from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';
import ConditionBuilder from './ConditionBuilder';
import {
  ACTION_TYPES,
  ANY_STATE,
  GUARD_TYPES,
  blankTransition,
  slugify,
  uniqueId,
  type Condition,
  type Workflow,
  type WorkflowTransition,
} from './workflowModel';

interface Props {
  workflow: Workflow;
  onChange: (transitions: WorkflowTransition[]) => void;
  readOnly?: boolean;
}

const INPUT =
  'px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';

export default function TransitionsEditor({ workflow, onChange, readOnly = false }: Props) {
  const { transitions, states } = workflow;
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const patch = (index: number, updates: Partial<WorkflowTransition>) =>
    onChange(transitions.map((t, i) => (i === index ? { ...t, ...updates } : t)));

  const add = () =>
    onChange([
      ...transitions,
      blankTransition(
        transitions.map((t) => t.id),
        states[0]?.id || '',
        states[states.length - 1]?.id || ''
      ),
    ]);

  const remove = (index: number) =>
    onChange(transitions.filter((_, i) => i !== index));

  const toggle = (index: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  const toggleSource = (index: number, stateId: string) => {
    const current = transitions[index].from;
    // The wildcard subsumes everything, so selecting it replaces the list.
    if (stateId === ANY_STATE) {
      patch(index, { from: current.includes(ANY_STATE) ? [] : [ANY_STATE] });
      return;
    }
    const withoutWildcard = current.filter((s) => s !== ANY_STATE);
    patch(index, {
      from: withoutWildcard.includes(stateId)
        ? withoutWildcard.filter((s) => s !== stateId)
        : [...withoutWildcard, stateId],
    });
  };

  const toggleGuard = (index: number, guardType: string) => {
    const guards = transitions[index].guards;
    patch(index, {
      guards: guards.some((g) => g.type === guardType)
        ? guards.filter((g) => g.type !== guardType)
        : [...guards, { type: guardType, config: {} }],
    });
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Transitions
          <span className="ml-2 font-normal text-xs text-gray-500 dark:text-gray-400">
            which moves are legal
          </span>
        </h3>
        <button
          onClick={add}
          disabled={readOnly || states.length < 2}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded text-accent hover:bg-accent/10 disabled:opacity-40"
          title={states.length < 2 ? 'Add a second state first' : undefined}
        >
          <Plus size={13} /> Add transition
        </button>
      </div>

      {transitions.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
          No transitions. Nothing will be able to change state — add at least one,
          or a single transition from <code>*</code> to keep it permissive.
        </p>
      )}

      <div className="space-y-1">
        {transitions.map((transition, index) => {
          const isOpen = expanded.has(index);
          // A transition leaving a decision node is a branch: its condition and
          // ordering are what decide where an item goes.
          const isBranch = transition.from.some((f) =>
            states.some((s) => s.id === f && s.kind === 'decision'));
          const extras = [
            isBranch ? (transition.default ? 'default branch' : 'conditional branch') : '',
            transition.guards.length ? `${transition.guards.length} guard${transition.guards.length === 1 ? '' : 's'}` : '',
            transition.requiredPermission ? `needs ${transition.requiredPermission}` : '',
            transition.requiredFields.length ? `${transition.requiredFields.length} required field${transition.requiredFields.length === 1 ? '' : 's'}` : '',
            transition.actions.length ? `${transition.actions.length} action${transition.actions.length === 1 ? '' : 's'}` : '',
          ].filter(Boolean);

          return (
            <div
              key={`${transition.id}-${index}`}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-750">
                <button onClick={() => toggle(index)} className="p-0.5 text-gray-400 shrink-0">
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {isBranch && (
                  <span title="Branch out of a decision node" className="shrink-0">
                    <GitFork size={12} className="text-amber-500" />
                  </span>
                )}

                <input
                  value={transition.name}
                  disabled={readOnly}
                  onChange={(e) => {
                    const name = e.target.value;
                    const derived = slugify(transition.name);
                    const keepInStep = transition.id === derived || transition.id.startsWith('move');
                    patch(index, {
                      name,
                      ...(keepInStep
                        ? {
                            id: uniqueId(
                              slugify(name),
                              transitions.filter((_, i) => i !== index).map((t) => t.id)
                            ),
                          }
                        : {}),
                    });
                  }}
                  placeholder="Name"
                  className={`${INPUT} w-32 text-xs shrink-0`}
                />

                <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate flex-1 min-w-0">
                  {transition.from.length
                    ? transition.from.map((s) => (s === ANY_STATE ? 'anywhere' : s)).join(', ')
                    : <span className="text-red-500">no source</span>}
                  {' → '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {transition.to || '?'}
                  </span>
                  {extras.length > 0 && (
                    <span className="ml-2 text-gray-400">· {extras.join(' · ')}</span>
                  )}
                </span>

                <select
                  value={transition.to}
                  disabled={readOnly}
                  onChange={(e) => patch(index, { to: e.target.value })}
                  className={`${INPUT} w-32 text-xs shrink-0`}
                  title="Target state"
                >
                  <option value="">to…</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>

                <button
                  onClick={() => remove(index)}
                  disabled={readOnly}
                  className="p-0.5 text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-40"
                  title="Remove transition"
                >
                  <Trash2 size={13} />
                </button>
              </div>

              {isOpen && (
                <div className="p-3 space-y-3 text-xs bg-white dark:bg-gray-800">
                  {/* Branch behaviour — only meaningful out of a decision node */}
                  {isBranch && (
                    <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-2 space-y-2">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Branch
                        </span>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(transition.default)}
                            disabled={readOnly}
                            onChange={(e) => patch(index, { default: e.target.checked })}
                            className="rounded border-gray-300 text-accent focus:ring-accent"
                          />
                          <span className="text-gray-700 dark:text-gray-300">
                            Default — taken when nothing else matches
                          </span>
                        </label>
                        <label className="flex items-center gap-1.5 ml-auto">
                          <span className="text-gray-500 dark:text-gray-400">Order</span>
                          <input
                            type="number"
                            value={transition.order ?? 0}
                            disabled={readOnly}
                            onChange={(e) => patch(index, { order: Number(e.target.value) || 0 })}
                            className={`${INPUT} w-14`}
                            title="Branches are evaluated low to high; the first match wins"
                          />
                        </label>
                      </div>

                      {transition.default ? (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          The default branch runs only after every other condition
                          has been tried, so it needs no condition of its own.
                        </p>
                      ) : (
                        <ConditionBuilder
                          condition={(transition.when ?? null) as Condition}
                          readOnly={readOnly}
                          onChange={(when) => patch(index, { when })}
                        />
                      )}
                    </div>
                  )}

                  {/* Sources */}
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Allowed from
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => toggleSource(index, ANY_STATE)}
                        disabled={readOnly}
                        className={clsx(
                          'px-2 py-0.5 rounded-full border text-[11px]',
                          transition.from.includes(ANY_STATE)
                            ? 'bg-accent text-white border-accent'
                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent'
                        )}
                        title="From any state"
                      >
                        anywhere (*)
                      </button>
                      {states.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => toggleSource(index, s.id)}
                          disabled={readOnly || transition.from.includes(ANY_STATE)}
                          className={clsx(
                            'px-2 py-0.5 rounded-full border text-[11px] disabled:opacity-30',
                            transition.from.includes(s.id)
                              ? 'bg-accent text-white border-accent'
                              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent'
                          )}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Guards */}
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Guards
                      <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                        — checked before the move commits
                      </span>
                    </p>
                    <div className="space-y-0.5">
                      {GUARD_TYPES.map((guard) => (
                        <label key={guard.type} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={transition.guards.some((g) => g.type === guard.type)}
                            disabled={readOnly}
                            onChange={() => toggleGuard(index, guard.type)}
                            className="rounded border-gray-300 text-accent focus:ring-accent"
                          />
                          <span className="text-gray-700 dark:text-gray-300">{guard.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Permissions and required fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Required permission
                      </label>
                      {/* Enforced: the mover must hold this RBAC permission at a scope
                          covering the item, on every path that moves it. The server
                          refuses an unknown permission. (The old "required role"
                          field was never enforced and is no longer offered.) */}
                      <input
                        value={transition.requiredPermission}
                        disabled={readOnly}
                        onChange={(e) => patch(index, { requiredPermission: e.target.value.trim() })}
                        placeholder="e.g. release:approve"
                        className={`${INPUT} w-full`}
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Required fields
                      </label>
                      <input
                        value={transition.requiredFields.join(', ')}
                        disabled={readOnly}
                        onChange={(e) =>
                          patch(index, {
                            requiredFields: e.target.value
                              .split(',')
                              .map((f) => f.trim())
                              .filter(Boolean),
                          })
                        }
                        placeholder="comma-separated"
                        className={`${INPUT} w-full`}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-gray-700 dark:text-gray-300">
                        Actions
                        <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                          — applied when the move succeeds
                        </span>
                      </p>
                      <button
                        disabled={readOnly}
                        onClick={() =>
                          patch(index, {
                            actions: [
                              ...transition.actions,
                              { type: 'stampNow', field: '', value: '' },
                            ],
                          })
                        }
                        className="text-accent hover:underline text-[11px] disabled:opacity-40"
                      >
                        + add action
                      </button>
                    </div>
                    {transition.actions.length === 0 && (
                      <p className="text-gray-400 dark:text-gray-500">None.</p>
                    )}
                    <div className="space-y-1">
                      {transition.actions.map((action, ai) => (
                        <div key={ai} className="flex items-center gap-1">
                          <select
                            value={action.type}
                            onChange={(e) =>
                              patch(index, {
                                actions: transition.actions.map((a, i) =>
                                  i === ai ? { ...a, type: e.target.value } : a
                                ),
                              })
                            }
                            className={`${INPUT} w-56`}
                          >
                            {ACTION_TYPES.map((a) => (
                              <option key={a.type} value={a.type}>{a.label}</option>
                            ))}
                          </select>
                          <input
                            value={action.field}
                            onChange={(e) =>
                              patch(index, {
                                actions: transition.actions.map((a, i) =>
                                  i === ai ? { ...a, field: e.target.value } : a
                                ),
                              })
                            }
                            placeholder="field"
                            className={`${INPUT} w-36`}
                          />
                          {action.type === 'setField' && (
                            <input
                              value={String(action.value ?? '')}
                              onChange={(e) =>
                                patch(index, {
                                  actions: transition.actions.map((a, i) =>
                                    i === ai ? { ...a, value: e.target.value } : a
                                  ),
                                })
                              }
                              placeholder="value"
                              className={`${INPUT} w-36`}
                            />
                          )}
                          <button
                            onClick={() =>
                              patch(index, {
                                actions: transition.actions.filter((_, i) => i !== ai),
                              })
                            }
                            className="p-0.5 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Id, last: rarely edited, but it is what the API stores. */}
                  <div>
                    <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Id
                    </label>
                    <input
                      value={transition.id}
                      disabled={readOnly}
                      onChange={(e) => patch(index, { id: slugify(e.target.value) })}
                      className={`${INPUT} w-48 font-mono`}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
